import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// Module-scope chart-config refs — avoid reallocating on every render.
const TT_CHART_MARGIN = { top: 8, right: 12, bottom: 0, left: 0 };
const TT_AXIS_TICK = { fontSize: 10 };
const TT_TOOLTIP_WRAPPER = { fontSize: 11 };

import {
  Music2, Link2, KeyRound, RefreshCw, Trash2, Loader2, ExternalLink,
  AlertOctagon, CheckCircle2, Users, Heart, MessageSquare, Share2, Play,
  UploadCloud, FileSpreadsheet, History,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const TT_ACCENT = "#00F2EA";   // TikTok cyan
const TT_PINK = "#FF0050";

function fmt(n) {
  const v = Number(n || 0);
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
  return v.toLocaleString();
}

function pct(v) {
  return (Number(v || 0) * 100).toFixed(2) + "%";
}

export default function ClientTikTokPage() {
  const { clientId } = useParams();
  const [params] = useSearchParams();
  const justConnected = params.get("status") === "connected";

  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get(`/clients/${clientId}/tiktok/status`),
        api.get(`/clients/${clientId}/tiktok/analytics?top_n=10`),
      ]);
      setStatus(s.data);
      setAnalytics(a.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load TikTok status");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (justConnected) {
      toast.success("TikTok account connected");
    }
  }, [justConnected]);

  const startOAuth = async () => {
    try {
      const { data } = await api.get(`/integrations/tiktok/oauth/start?client_id=${clientId}`);
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start TikTok OAuth (is TIKTOK_CLIENT_KEY set?)");
    }
  };

  const saveManualToken = async () => {
    const t = (manualToken || "").trim();
    if (!t) { toast.error("Paste an access token"); return; }
    setSavingManual(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/tiktok/connect/manual`,
        { access_token: t }
      );
      toast.success(`Connected as open_id=${(data.open_id || "").slice(0, 10)}…`);
      setManualOpen(false);
      setManualToken("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Token rejected by TikTok");
    } finally { setSavingManual(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/tiktok/sync`);
      toast.success(`Synced · ${data.videos_count || 0} videos`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const disconnect = async () => {
    try {
      await api.delete(`/clients/${clientId}/tiktok/disconnect`);
      toast.success("Disconnected");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect");
    }
  };

  if (loading && !status) {
    return (
      <div className="p-8 text-sm text-zinc-500 flex items-center gap-2" data-testid="tiktok-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading TikTok status…
      </div>
    );
  }

  const connected = !!status?.connected;
  const profile = status?.profile;
  const kpis = analytics?.kpis;

  return (
    <div className="space-y-6" data-testid="client-tiktok-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm flex items-center justify-center text-white"
            style={{ background: `linear-gradient(135deg, ${TT_PINK}, ${TT_ACCENT})` }}>
            <Music2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">TikTok</h1>
            <p className="text-xs text-zinc-500">Organic profile, follower growth, top videos · synced daily at 02:30 Sydney</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {connected ? (
            <>
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="tiktok-status-pill">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                {status.status === "connected" ? "Connected" : (status.status || "Connected")}
              </Badge>
              <span className="text-[11px] font-mono text-zinc-500">
                via {status.auth_method === "oauth" ? "OAuth" : "manual token"}
              </span>
              <Button size="sm" variant="outline" className="rounded-sm"
                onClick={syncNow} disabled={syncing} data-testid="tiktok-sync-now">
                {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                         : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Sync now
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                    data-testid="tiktok-disconnect">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Disconnect
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-sm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Disconnect TikTok?</AlertDialogTitle>
                    <AlertDialogDescription>
                      The stored access token will be revoked and removed.
                      Cached profile + videos will also be wiped from the database.
                      You can reconnect any time.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                    <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                      onClick={disconnect} data-testid="tiktok-disconnect-confirm">
                      Disconnect
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <>
              <Button size="sm" className="rounded-sm text-white"
                style={{ background: `linear-gradient(135deg, ${TT_PINK}, ${TT_ACCENT})` }}
                onClick={startOAuth} disabled={!status?.configured}
                data-testid="tiktok-connect-oauth">
                <Link2 className="h-3.5 w-3.5 mr-1.5" /> Connect TikTok (OAuth)
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm"
                onClick={() => setManualOpen(true)} data-testid="tiktok-connect-manual">
                <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Paste access token
              </Button>
            </>
          )}
        </div>
      </div>

      {!status?.configured && !connected && (
        <Card className="rounded-sm border-amber-200 bg-amber-50/60" data-testid="tiktok-not-configured">
          <CardContent className="p-4 text-[13px] text-amber-900 space-y-2">
            <div className="flex items-center gap-2 font-semibold">
              <AlertOctagon className="h-4 w-4" /> TikTok app not configured yet
            </div>
            <p>To enable the <strong>Connect TikTok</strong> OAuth flow, add these to <code className="bg-white px-1 rounded">/app/backend/.env</code> and restart the backend:</p>
            <ul className="list-disc list-inside font-mono text-[11px] pl-2 text-amber-800">
              <li>TIKTOK_CLIENT_KEY=<em>your client key from developers.tiktok.com</em></li>
              <li>TIKTOK_CLIENT_SECRET=<em>your client secret</em></li>
              <li>TIKTOK_REDIRECT_URI=<em>https://&lt;your-domain&gt;/api/integrations/tiktok/oauth/callback</em></li>
            </ul>
            <p className="text-[11px]">Steps to create the app: 1) Sign up at <a className="underline" href="https://developers.tiktok.com" target="_blank" rel="noreferrer">developers.tiktok.com</a> 2) <em>Manage apps → Connect an app</em> 3) Add the <strong>Login Kit</strong> product 4) Under <strong>Scopes</strong> add <code>user.info.basic</code>, <code>user.info.profile</code>, <code>user.info.stats</code>, <code>video.list</code> 5) Register the redirect URI above 6) Copy the Client key + Client secret.</p>
            <p className="text-[11px]">Meanwhile, you can still test by pasting a temporary sandbox access token via <strong>Paste access token</strong>.</p>
          </CardContent>
        </Card>
      )}

      {status?.last_error && (
        <Card className="rounded-sm border-red-200 bg-red-50/60" data-testid="tiktok-last-error">
          <CardContent className="p-3 flex items-start gap-2 text-[12px]">
            <AlertOctagon className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="font-mono text-red-800 break-words">{status.last_error}</div>
          </CardContent>
        </Card>
      )}

      {/* Profile card */}
      {connected && profile && (
        <Card className="rounded-sm border-zinc-200" data-testid="tiktok-profile-card">
          <CardContent className="p-5 flex gap-4 items-center flex-wrap">
            {profile.avatar_url && (
              <img src={profile.avatar_url} alt={profile.display_name}
                className="h-20 w-20 rounded-sm object-cover border border-zinc-200" />
            )}
            <div className="flex-1 min-w-[240px]">
              <div className="font-display font-bold text-xl tracking-tight" data-testid="tiktok-display-name">
                {profile.display_name || "—"}
              </div>
              {profile.username && (
                <div className="text-[12px] font-mono text-zinc-500">@{profile.username}</div>
              )}
              {profile.bio_description && (
                <p className="text-[12px] text-zinc-700 mt-1.5 max-w-2xl">{profile.bio_description}</p>
              )}
              {profile.profile_deep_link && (
                <a href={profile.profile_deep_link} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-[#FF0050] mt-1.5 hover:underline">
                  View on TikTok <ExternalLink className="h-2.5 w-2.5" />
                </a>
              )}
            </div>
            <div className="text-right text-[11px] text-zinc-500 font-mono">
              {status?.last_sync_at && (
                <>last synced<br />{new Date(status.last_sync_at).toLocaleString()}</>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI tiles */}
      {connected && kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="tiktok-kpis">
          <Kpi icon={Users} label="Followers" value={fmt(kpis.followers)} testId="kpi-followers" />
          <Kpi icon={Heart} label="Total likes" value={fmt(kpis.total_likes)} testId="kpi-likes" />
          <Kpi icon={Play} label="Total views" value={fmt(kpis.total_views)} testId="kpi-views" />
          <Kpi icon={Music2} label="Videos" value={fmt(kpis.total_videos)} testId="kpi-videos" />
          <Kpi icon={Share2} label="Avg engagement" value={pct(kpis.avg_engagement_rate)} testId="kpi-engagement" />
        </div>
      )}

      {/* Top videos */}
      {connected && analytics?.top_videos?.length > 0 && (
        <Card className="rounded-sm border-zinc-200" data-testid="tiktok-top-videos-card">
          <CardContent className="p-0 overflow-hidden">
            <div className="px-5 py-3 bg-zinc-50/60 border-b border-zinc-100 flex items-center justify-between">
              <div className="label-overline">Top videos · by views</div>
              <span className="text-[10px] font-mono text-zinc-400">
                {analytics.top_videos.length} of {analytics.video_count_in_db}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white border-b border-zinc-100">
                  <tr className="text-left">
                    <th className="px-3 py-2 label-overline">Cover</th>
                    <th className="px-3 py-2 label-overline">Title / description</th>
                    <th className="px-3 py-2 label-overline text-right">Views</th>
                    <th className="px-3 py-2 label-overline text-right">Likes</th>
                    <th className="px-3 py-2 label-overline text-right">Comments</th>
                    <th className="px-3 py-2 label-overline text-right">Shares</th>
                    <th className="px-3 py-2 label-overline">Posted</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_videos.map((v) => (
                    <tr key={v.video_id || v.id} className="border-b border-zinc-50"
                        data-testid={`tiktok-video-row-${v.video_id || v.id}`}>
                      <td className="px-3 py-2">
                        {v.cover_image_url ? (
                          <a href={v.share_url || "#"} target="_blank" rel="noreferrer">
                            <img src={v.cover_image_url} alt=""
                              className="h-16 w-10 object-cover rounded-sm border border-zinc-200" />
                          </a>
                        ) : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-[12px] max-w-[360px]">
                        <div className="font-medium truncate" title={v.title || v.video_description}>
                          {v.title || v.video_description || "Untitled"}
                        </div>
                        {v.share_url && (
                          <a href={v.share_url} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-0.5 text-[10px] font-mono text-zinc-500 hover:text-[#FF0050]">
                            open ↗
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{fmt(v.view_count)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{fmt(v.like_count)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{fmt(v.comment_count)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{fmt(v.share_count)}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">
                        {v.create_time ? new Date(v.create_time * 1000).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {connected && analytics && analytics.top_videos?.length === 0 && (
        <Card className="rounded-sm border-zinc-200" data-testid="tiktok-no-videos">
          <CardContent className="p-6 text-center text-sm text-zinc-500">
            No videos have been synced yet. Click <strong>Sync now</strong> to pull the latest posts from TikTok.
          </CardContent>
        </Card>
      )}

      {/* CSV history panel — always available, even without OAuth */}
      <TikTokCsvHistoryPanel clientId={clientId} onChanged={load} />

      {/* Manual access token dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="tiktok-manual-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Paste TikTok access token</DialogTitle>
            <DialogDescription className="text-[12px] text-zinc-500">
              Manual fallback for sandbox / non-OAuth flows. The token will
              be validated against TikTok&apos;s <code>/v2/user/info</code> endpoint and
              stored encrypted. Note that long-lived manual tokens do <strong>not</strong>
              auto-refresh — when they expire you&apos;ll need to paste a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="label-overline">Access token</Label>
            <Input type="password" value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              placeholder="act.…"
              className="rounded-sm font-mono"
              data-testid="tiktok-manual-token-input"
              autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm"
              onClick={() => setManualOpen(false)}
              data-testid="tiktok-manual-cancel">Cancel</Button>
            <Button className="rounded-sm bg-[#FF0050] hover:bg-[#cc0040] text-white"
              onClick={saveManualToken} disabled={savingManual}
              data-testid="tiktok-manual-save">
              {savingManual
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Validating…</>
                : "Save token"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <Card className="rounded-sm border-zinc-200" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon className="h-3.5 w-3.5 text-zinc-500" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
        </div>
        <div className="font-display font-bold text-2xl tracking-tight text-zinc-900">{value}</div>
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------------------
// CSV history panel — upload TikTok Studio exports for back-dated data
// --------------------------------------------------------------------------

const HISTORY_SERIES = [
  { key: "video_views", label: "Video views", color: "#FF0050" },
  { key: "profile_views", label: "Profile views", color: "#00F2EA" },
  { key: "followers", label: "Followers", color: "#10B981" },
  { key: "likes", label: "Likes", color: "#F59E0B" },
];

function TikTokCsvHistoryPanel({ clientId, onChanged }) {
  const fileRef = React.useRef();
  const [uploads, setUploads] = useState([]);
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, h] = await Promise.all([
        api.get(`/clients/${clientId}/tiktok/uploads`),
        api.get(`/clients/${clientId}/tiktok/history?days=365`),
      ]);
      setUploads(u.data?.uploads || []);
      setHistory(h.data?.rows || []);
    } catch (e) {
      // History endpoint requires connected — surface only real errors
      if (e.response?.status !== 404) {
        toast.error(e.response?.data?.detail || "Failed to load CSV history");
      }
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const pickFile = () => fileRef.current?.click();

  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File must be 20 MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/clients/${clientId}/tiktok/uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Ingested ${data.rows_ingested} ${data.kind === "account" ? "daily rows" : "videos"}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteUpload = async (uploadId) => {
    try {
      await api.delete(`/clients/${clientId}/tiktok/uploads/${uploadId}`);
      toast.success("Upload removed");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete upload");
    }
  };

  const chartData = history.map((r) => ({
    date: r.date,
    video_views: r.metrics?.video_views || 0,
    profile_views: r.metrics?.profile_views || 0,
    followers: r.metrics?.followers || 0,
    likes: r.metrics?.likes || 0,
  }));

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="tiktok-csv-history-panel">
      <CardContent className="p-0 overflow-hidden">
        <div className="px-5 py-3 bg-zinc-50/60 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <History className="h-3.5 w-3.5 text-zinc-500" />
            <div className="label-overline">Historical data · CSV uploads</div>
            <span className="text-[11px] font-mono text-zinc-400">
              ({uploads.length} upload{uploads.length === 1 ? "" : "s"} · {history.length} day{history.length === 1 ? "" : "s"})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls"
              className="hidden" onChange={onFilePicked}
              data-testid="tiktok-csv-file-input" />
            <Button size="sm"
              className="rounded-sm bg-[#FF0050] hover:bg-[#cc0040] text-white"
              onClick={pickFile} disabled={uploading}
              data-testid="tiktok-csv-upload-btn">
              {uploading
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading…</>
                : <><UploadCloud className="h-3.5 w-3.5 mr-1.5" /> Upload TikTok export</>}
            </Button>
          </div>
        </div>

        <div className="px-5 py-3 text-[11px] text-zinc-600 border-b border-zinc-100 bg-amber-50/40">
          The Display API only returns the latest 20 videos. Upload your monthly
          TikTok Studio CSV / XLSX exports here to back-fill historical daily
          analytics (profile views, video views, likes, comments, shares, followers)
          and / or per-video stats. We auto-detect which export type you uploaded.
        </div>

        {/* Daily trend chart */}
        {chartData.length > 1 && (
          <div className="px-5 py-4 border-b border-zinc-100" data-testid="tiktok-history-chart">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData} margin={TT_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={TT_AXIS_TICK} />
                <YAxis tick={TT_AXIS_TICK} />
                <Tooltip wrapperStyle={TT_TOOLTIP_WRAPPER} />
                {HISTORY_SERIES.map((s) => (
                  <Line key={s.key} type="monotone" dataKey={s.key}
                    stroke={s.color} strokeWidth={2} dot={false} name={s.label} />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-3 flex-wrap text-[11px] mt-2 text-zinc-600">
              {HISTORY_SERIES.map((s) => (
                <span key={s.key} className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-3 rounded-sm" style={{ background: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Uploads list */}
        {loading ? (
          <div className="p-6 text-center text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
          </div>
        ) : uploads.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500" data-testid="tiktok-csv-empty">
            No uploads yet. Drop a TikTok Studio CSV/XLSX above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white border-b border-zinc-100">
                <tr className="text-left">
                  <th className="px-3 py-2 label-overline">Filename</th>
                  <th className="px-3 py-2 label-overline">Kind</th>
                  <th className="px-3 py-2 label-overline">Period</th>
                  <th className="px-3 py-2 label-overline text-right">Rows</th>
                  <th className="px-3 py-2 label-overline">Uploaded</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {uploads.map((u) => (
                  <tr key={u.upload_id} className="border-b border-zinc-50"
                      data-testid={`tiktok-csv-row-${u.upload_id}`}>
                    <td className="px-3 py-2 text-[12px] truncate max-w-[280px]">
                      <span className="inline-flex items-center gap-1.5">
                        <FileSpreadsheet className="h-3.5 w-3.5 text-zinc-400" />
                        {u.filename}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider font-mono">
                        {u.kind}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-700">
                      {u.period ? `${u.period.start} → ${u.period.end}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px]">{u.row_count}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">
                      {u.uploaded_at ? new Date(u.uploaded_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost"
                        className="rounded-sm text-zinc-500 hover:text-red-600 h-7 px-2"
                        onClick={() => deleteUpload(u.upload_id)}
                        data-testid={`tiktok-csv-delete-${u.upload_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
